import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@treams/treams-icons';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';

import { Button } from './Button';
import { Skeleton } from './Skeleton';
import { Paragraph } from './Text';

const TableContainer = styled(Box)`
	background: #ffffff;
	border: 1px solid #c2cad2;
	box-shadow: 0px 2px 4px rgba(194, 202, 210, 0.25);
	border-radius: 4px;
`;

export function Table(props: PropsWithChildren) {
	return <TableContainer>{props.children}</TableContainer>;
}

const TableCellContainer = styled(Flex)<{ interactive?: boolean }>`
	padding: 0px 16px;
	line-height: 20px;

	${(props) =>
		props.interactive &&
		`
		cursor: pointer;

		:hover {
			color: ${props.theme.colors.slateBlue};
		}
	`}
`;

const TableCellSortIndicator = styled(Flex)<{ sorted?: 'asc' | 'desc' }>`
	svg {
		width: 16px;
		height: 16px;
	}

	${(props) =>
		props.sorted === 'asc' &&
		`
		svg {
			transform: rotate(180deg);
		}
	`}
`;

export function TableCell(
	props: PropsWithChildren<{
		align?: 'left' | 'right';
		basis: number;
		sorted?: 'asc' | 'desc';
		onClick?: () => void;
	}>,
) {
	return (
		<TableCellContainer
			flexBasis={(props.basis * 100).toFixed(0) + '%'}
			alignItems="center"
			justifyContent={props.align === 'right' ? 'flex-end' : 'flex-start'}
			interactive={props.onClick !== undefined}
			onClick={props.onClick}
		>
			{props.children}
			{props.sorted && (
				<TableCellSortIndicator sorted={props.sorted}>
					<ChevronDownIcon />
				</TableCellSortIndicator>
			)}
		</TableCellContainer>
	);
}

Table.Cell = TableCell;

export function TableCellSkeleton(props: PropsWithChildren<{ basis: number }>) {
	return (
		<TableCellContainer flexBasis={(props.basis * 100).toFixed(0) + '%'}>
			<Skeleton.Box height={20} flexGrow={1} />
		</TableCellContainer>
	);
}

TableCell.Skeleton = TableCellSkeleton;

const TableRowContainer = styled(Flex)`
	padding: 8px 0px;
	border-bottom: 1px solid ${(props) => props.theme.colors.whiteBlue};
	color: ${(props) => props.theme.colors.slateBlue};

	&:last-child {
		border-bottom: none;
	}
`;

export function TableRow(props: PropsWithChildren) {
	return <TableRowContainer>{props.children}</TableRowContainer>;
}

Table.Row = TableRow;

const TableHeaderContainer = styled(Flex)`
	padding: 8px 0px;
	background: ${(props) => props.theme.colors.whiteBlueLight};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	border-bottom: 1px solid #c2cad2;

	&:last-child {
		border-bottom: none;
		border-radius: 8px;
	}

	${TableCellContainer} {
		font-weight: bold;
	}
`;

export function TableHeader(props: PropsWithChildren) {
	return <TableHeaderContainer>{props.children}</TableHeaderContainer>;
}

Table.Header = TableHeader;

const TableFooterContainer = styled(Flex)`
	padding: 8px 0px;
	border-top: 1px solid ${(props) => props.theme.colors.whiteBlue};

	${TableCellContainer} {
		font-weight: bold;
	}
`;

export function TableFooter(props: PropsWithChildren) {
	return <TableFooterContainer>{props.children}</TableFooterContainer>;
}

Table.Footer = TableFooter;

function TablePagination(props: {
	disabled: {
		previous: boolean;
		next: boolean;
	};
	pagination: {
		currentPage: number;
		perPage: number;
		max: number;
	};
	onPrevious: () => void;
	onNext: () => void;
}) {
	const { t } = useTranslation();
	const { pagination } = props;

	return (
		<Flex py={2} alignItems="center" justifyContent="space-between">
			<Box>
				<Paragraph>
					{t('table.pagination.ofThe', {
						progress: `${(pagination.currentPage - 1) * pagination.perPage + 1} - ${Math.min(
							pagination.max,
							pagination.currentPage * pagination.perPage,
						)}`,
						max: pagination.max,
					})}
				</Paragraph>
			</Box>

			<Flex>
				<Button
					small
					type="card"
					title={t('table.pagination.previous')}
					disabled={props.disabled.previous}
					onClick={() => props.onPrevious()}
					icon={<ChevronLeftIcon />}
				/>
				<Button
					ml={2}
					small
					type="card"
					title={t('table.pagination.next')}
					disabled={props.disabled.next}
					onClick={() => props.onNext()}
					rightIcon={<ChevronRightIcon />}
				/>
			</Flex>
		</Flex>
	);
}

Table.Pagination = TablePagination;

export function TablePaging(props: {
	pageInfo?: graphql.PageInfo;
	onPaginate: (pagination: Pagination) => void;
}) {
	const { t } = useTranslation();

	return (
		<Flex mt={3} justifyContent="flex-end">
			<Box mr={2}>
				<Button
					title={t('common.back')}
					small
					type="card"
					disabled={!props.pageInfo?.hasPreviousPage}
					onClick={() => {
						props.onPaginate({ last: 10, before: props.pageInfo?.startCursor });
					}}
				/>
			</Box>
			<Button
				title={t('common.next')}
				small
				type="card"
				disabled={!props.pageInfo?.hasNextPage}
				onClick={() => {
					props.onPaginate({ first: 10, after: props.pageInfo?.endCursor });
				}}
			/>
		</Flex>
	);
}

Table.Paging = TablePaging;

export type Pagination =
	| {
			first: number;
			after?: string | null;
	  }
	| {
			last: number;
			before?: string | null;
	  };
